import { createContext, useState, useEffect, useContext, useCallback, useMemo, ReactElement } from 'react';
import { useRestaurantContext } from '../RestaurantContext';
import { getEventSettings, updateEventSettings } from '../../api/eventSettings';
import { deleteEventMedia, getEventMedia, reorderEventMedia, uploadEventMedia } from '../../api/eventMedia';
import {
  EventMediaInterface,
  EventSettingsInterface,
  UpdateEventSettingsRequest
} from '../../types/EventSettingsInterface';

interface EventSettingsContextInterface {
  isLoading: boolean;
  hasError: boolean;
  settings: EventSettingsInterface | null;
  media: EventMediaInterface[];
  loadEventData: Function;
  saveSettings: Function;
  uploadMedia: Function;
  removeMedia: Function;
  reorderMedia: Function;
  retry: Function;
}

const EventSettingsContext = createContext<EventSettingsContextInterface>(null);

const byListOrder = (a: EventMediaInterface, b: EventMediaInterface) => a.listOrder - b.listOrder;

const EventSettingsProvider = ({ children }: { children: ReactElement }) => {
  const { currentRestaurant } = useRestaurantContext();
  const [settings, setSettings] = useState<EventSettingsInterface | null>(null);
  const [media, setMedia] = useState<EventMediaInterface[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);
  const [loadedRestaurantID, setLoadedRestaurantID] = useState<number | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Load (and reload on restaurant switch) once the page has asked for data via loadEventData().
  useEffect(() => {
    let canceled = false;
    if (initialLoad && currentRestaurant && currentRestaurant.restaurantID !== loadedRestaurantID) {
      setIsLoading(true);
      setHasError(false);
      Promise.all([getEventSettings(), getEventMedia()])
        .then(([_settings, _media]) => {
          if (canceled) return;
          setSettings(_settings);
          setMedia([..._media].sort(byListOrder));
          setLoadedRestaurantID(currentRestaurant.restaurantID);
          setIsLoading(false);
        })
        .catch((error) => {
          if (canceled) return;
          if (error?.response?.status !== 401) {
            setHasError(true);
          }
          setIsLoading(false);
        });
    }
    return () => {
      canceled = true;
    };
  }, [initialLoad, currentRestaurant, loadedRestaurantID, reloadToken]);

  const loadEventData = useCallback(() => {
    setInitialLoad(true);
  }, []);

  // Recover from a failed initial load: bump a token the load effect depends on (loadedRestaurantID
  // is still null after an error, so the effect's guard re-fires and refetches).
  const retry = useCallback(() => {
    setHasError(false);
    setReloadToken((token) => token + 1);
  }, []);

  const saveSettings = useCallback(async (update: UpdateEventSettingsRequest) => {
    const updated = await updateEventSettings(update);
    setSettings(updated);
  }, []);

  const uploadMedia = useCallback(async (images: Blob[], video?: Blob | null) => {
    // POST returns just the newly-created media (with their assigned listOrder) — merge it in
    // rather than making a second GET round-trip.
    const created = await uploadEventMedia(images, video);
    setMedia((prev) => [...prev, ...created].sort(byListOrder));
  }, []);

  const removeMedia = useCallback(async (eventMediaID: number) => {
    await deleteEventMedia(eventMediaID);
    setMedia((prev) => prev.filter((item) => item.eventMediaID !== eventMediaID));
  }, []);

  const reorderMedia = useCallback(async (orderedMedia: EventMediaInterface[]) => {
    const withOrder = orderedMedia.map((item, index) => ({ ...item, listOrder: index }));
    setMedia(withOrder); // optimistic — reflect the new order immediately
    try {
      await reorderEventMedia(withOrder.map(({ eventMediaID, listOrder }) => ({ eventMediaID, listOrder })));
    } catch (error) {
      // Roll back to the server's truth if the reorder didn't persist.
      const refreshed = await getEventMedia();
      setMedia([...refreshed].sort(byListOrder));
      throw error;
    }
  }, []);

  const value = useMemo(
    () => ({
      isLoading,
      hasError,
      settings,
      media,
      loadEventData,
      saveSettings,
      uploadMedia,
      removeMedia,
      reorderMedia,
      retry
    }),
    [isLoading, hasError, settings, media, loadEventData, saveSettings, uploadMedia, removeMedia, reorderMedia, retry]
  );

  return <EventSettingsContext.Provider value={value}>{children}</EventSettingsContext.Provider>;
};

const useEventSettingsContext = () => {
  const context = useContext(EventSettingsContext);
  if (!context) {
    throw new Error(`useEventSettingsContext must be used within the EventSettingsProvider component`);
  }
  return context;
};

export { EventSettingsProvider as default, useEventSettingsContext };
